// 16-point compass direction codes (skipping I and O)
export const DIRECTION_LETTERS = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H',
  'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R',
];

export const DIRECTION_LABELS = {
  A: 'N (0°)',
  B: 'NNE (22.5°)',
  C: 'NE (45°)',
  D: 'ENE (67.5°)',
  E: 'E (90°)',
  F: 'ESE (112.5°)',
  G: 'SE (135°)',
  H: 'SSE (157.5°)',
  J: 'S (180°)',
  K: 'SSW (202.5°)',
  L: 'SW (225°)',
  M: 'WSW (247.5°)',
  N: 'W (270°)',
  P: 'WNW (292.5°)',
  Q: 'NW (315°)',
  R: 'NNW (337.5°)',
};

export const DIRECTION_BEARINGS = {
  A: 0, B: 22.5, C: 45, D: 67.5,
  E: 90, F: 112.5, G: 135, H: 157.5,
  J: 180, K: 202.5, L: 225, M: 247.5,
  N: 270, P: 292.5, Q: 315, R: 337.5,
};

export const COURSE_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

// 8-point compass sectors for NAMED courses. DBSC's 16x8 letter grid forces a
// directionCode+courseNumber identity that doesn't fit clubs (e.g. Cork) that
// number courses globally (1-31) and group them by wind sector. Named courses
// carry a free-form `windDirection` string (e.g. 'NE') that the app already
// deserializes (course_model.dart) and renders verbatim — no app change needed.
// (ra-gbrd.19)
export const WIND_SECTORS = [
  { code: 'N', label: 'N (0°)' },
  { code: 'NE', label: 'NE (45°)' },
  { code: 'E', label: 'E (90°)' },
  { code: 'SE', label: 'SE (135°)' },
  { code: 'S', label: 'S (180°)' },
  { code: 'SW', label: 'SW (225°)' },
  { code: 'W', label: 'W (270°)' },
  { code: 'NW', label: 'NW (315°)' },
];

// Lookup from a named course's 8-point wind sector code to its display label.
export const WIND_SECTOR_LABELS = Object.fromEntries(
  WIND_SECTORS.map((s) => [s.code, s.label]),
);

// Map an 8-point named-course wind sector to the 16-point grid letter that
// shares its bearing (N→A, NE→C, E→E, …). Lets the Course Viewer's grid Wind
// filter also surface named courses tagged with that sector — the 8 in-between
// grid letters (NNE/ENE/…) simply have no named-course equivalent. (ra-gbrd.35)
export const SECTOR_TO_DIRECTION_LETTER = {
  N: 'A',
  NE: 'C',
  E: 'E',
  SE: 'G',
  S: 'J',
  SW: 'L',
  W: 'N',
  NW: 'Q',
};

/**
 * Human-facing label for a course in viewer/list surfaces.
 *
 * Grid courses are identified by their compass cell (directionCode +
 * courseNumber, e.g. "C-3"). NAMED courses (directionCode == null) have no grid
 * cell — their identity is the free-form `name` (e.g. Cork Week's "Course 5").
 * Rendering the grid template for a named course yields "null-undefined", so the
 * custom name must be used instead. (ra-gbrd.35)
 */
export function courseDisplayLabel(course) {
  if (!course) return '';
  if (course.directionCode == null) {
    return course.name || 'Named course';
  }
  return `${course.directionCode}-${course.courseNumber}`;
}

/**
 * Does a course match the viewer's grid Wind-direction filter (a 16-point
 * letter)? Grid courses match on directionCode; named courses match when their
 * wind sector maps to the selected letter. A named course with no wind sector
 * never matches a specific direction filter. (ra-gbrd.35)
 */
export function courseMatchesDirection(course, directionLetter) {
  if (!directionLetter) return true;
  if (course.directionCode != null) {
    return course.directionCode === directionLetter;
  }
  return SECTOR_TO_DIRECTION_LETTER[course.windDirection] === directionLetter;
}
